import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { LoginCredentials, RegisterData, UpdateProfileData, AuthResponse, User } from '../types/auth.js';

const API_URL = import.meta.env.VITE_API_URL || '/api';

console.log('[Web API] Initializing API with base URL:', API_URL);

export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  timeout: 10000, // 10 seconds timeout
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Skip adding token for login/register requests
    if (config.url?.includes('/auth/') || config.url?.includes('/login') || config.url?.includes('/register')) {
      return config;
    }
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log('[Web API] API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      params: config.params,
      data: config.data,
    });
    
    return config;
  },
  (error) => {
    console.error('[Web API] Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log('[Web API] API Response:', {
      status: response.status,
      url: response.config.url,
      data: response.data,
    });
    return response;
  },
  (error: AxiosError) => {
    console.error('[Web API] API Error:', {
      message: error.message,
      status: error.response?.status,
      url: error.config?.url,
      response: error.response?.data,
    });
    
    if (error.response?.status === 401) {
      // Handle unauthorized errors
      console.error('[Web API] Authentication error, redirecting to login');
      // You might want to redirect to login here
      // window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Helper function to handle API errors
export function handleApiError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      return error.response.data?.message || error.response.statusText || 'An error occurred';
    } else if (error.request) {
      // The request was made but no response was received
      return '[Web API] No response from server. Please check your connection.';
    }
  }
  // Something happened in setting up the request that triggered an Error
  return '[Web API] An unexpected error occurred';
}

// Auth API
export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    try {
      const response = await api.post('/auth/login', credentials);
      console.log('[Web API] Login API response:', response.data);
      
      const { user, token, isFirstLogin } = response.data;
      
      if (!user || !token) {
        console.error('[Web API] Invalid login response - missing user or token');
        throw new Error('[Web API] Invalid login response - missing user or token');
      }
      
      // Ensure the user object has all required fields
      if (!user.id || !user.email || !user.role) {
        console.error('[Web API] Invalid user data in login response:', user);
        throw new Error('[Web API] Invalid user data in login response');
      }
      
      return {
        user: {
          id: user.id,
          email: user.email,
          name: user.name || null,
          company: user.company || null,
          site: user.site || 'HQ',
          group: user.group || 'test-team',
          role: user.role,
          sector: Array.isArray(user.sector) && user.sector.length > 0 ? user.sector : ['EPC'],
          isActive: user.isActive !== undefined ? user.isActive : true,
          lastLogin: user.lastLogin || null,
          createdAt: user.createdAt || new Date().toISOString(),
          updatedAt: user.updatedAt || new Date().toISOString(),
        },
        token,
        isFirstLogin: isFirstLogin || false
      };
    } catch (error) {
      console.error('[Web API] Login API error:', error);
      throw error;
    }
  },
  
  register: async (userData: RegisterData): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', userData);
    const { user, token } = response.data;
    
    if (!user || !token) {
      throw new Error('[Web API] Invalid registration response - missing user or token');
    }
    
    return {
      user,
      token
    };
  },
};

// Settings API
export const settingsApi = {
  getRegistrationStatus: async (): Promise<boolean> => {
    const response = await api.get('/auth/registration-status');
    return response.data.registrationEnabled;
  },
};

// User API
export const userApi = {
  getMe: (): Promise<User> => api.get('/users/me').then((res) => res.data),
  
  updateMe: (data: UpdateProfileData): Promise<User> =>
    api.put('/users/me', data).then((res) => res.data),
};

// Admin API
export const adminApi = {
  getUsers: async (): Promise<User[]> => {
    try {
      console.log('[Web API] Fetching users from:', `${api.defaults.baseURL}/admin/users`);
      const response = await api.get('/admin/users', { params: { limit: 100 } });
      console.log('[Web API] Users response:', response);
      
      if (!response.data) {
        console.error('[Web API] No data in response');
        return [];
      }
      
      // Handle paginated response format: { data: User[], meta: { total, page, limit, totalPages } }
      const users = Array.isArray(response.data) ? response.data : 
                   (response.data.data && Array.isArray(response.data.data)) ? response.data.data : [];
      
      console.log(`[Web API] Fetched ${users.length} users`);
      return users;
    } catch (error: unknown) {
      const axiosError = error as {
        message: string;
        response?: {
          status?: number;
          data?: any;
        };
        config?: {
          url?: string;
          method?: string;
        };
      };
      
      console.error('[Web API] Error in getUsers:', {
        message: axiosError.message,
        status: axiosError.response?.status,
        data: axiosError.response?.data,
        url: axiosError.config?.url,
        method: axiosError.config?.method,
      });
      throw error;
    }
  },
  
  updateUser: async (id: string, data: Partial<User>): Promise<User> => {
    try {
      const response = await api.put(`/admin/users/${id}`, data);
      return response.data.data ?? response.data;
    } catch (error) {
      console.error(`[Web API] Error updating user ${id}:`, error);
      throw error;
    }
  },
  
  createUser: async (data: {
    email: string;
    password: string;
    name?: string;
    company?: string;
    site?: string;
    group?: string;
    role?: string;
    sector?: string[];
  }): Promise<User> => {
    try {
      const response = await api.post('/admin/users', data);
      return response.data.data ?? response.data;
    } catch (error) {
      console.error('[Web API] Error creating user:', error);
      throw error;
    }
  },

  deleteUser: async (id: string): Promise<void> => {
    try {
      await api.delete(`/admin/users/${id}`);
    } catch (error) {
      console.error(`[Web API] Error deleting user ${id}:`, error);
      throw error;
    }
  },
  
  updateRegistrationSetting: async (enabled: boolean): Promise<boolean> => {
    const response = await api.put('/admin/settings/registration', { enabled });
    return response.data.registrationEnabled;
  },

  exportUsers: async (): Promise<Blob> => {
    try {
      const response = await api.get('/admin/export/users', { 
        responseType: 'blob' 
      });
      return new Blob([response.data], { type: 'text/csv' });
    } catch (error) {
      console.error('[Web API] Error exporting users:', error);
      throw error;
    }
  },
};
